import React from 'react';
import ListItem from '@mui/material/ListItem';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import { Divider, IconButton, ListItemButton, Stack, Typography } from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import VerticalHamburgerMenu from '../../common/controls/VerticalHamburgerMenu';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import { useInventoryItems } from '../../../hooks/inventory/useInventoryItems';
import { useDeleteInventoryItem } from '../../../hooks/inventory/useDeleteInventoryItem';
import SearchBox from '../../common/forms/SearchBox';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { getInventoryItemById } from '../../../api/inventoryService';
import InventoryItemForm from '../forms/InventoryItemForm';
import InventoryItemView from './InventoryItemView';
import InventoryTransactionForm from '../forms/InventoryTransactionForm';
import InventoryTransactionView from './InventoryTransactionView';
import { getInventoryTransactionById } from '../../../api/inventoryService';
import { removeZeroDecimals } from '../../../utils/removeZeroDecimals';
import RequirePermission from '../../RequirePermission';
import { userHasAccess } from '../../../utils/userHasAccess';
import { SUB_MODULES } from '../../../constants/subModulesConstants';
import { checkExpiryDate } from '../../../utils/checkExpiryDate';

export default function InventoryItems({mode = 'list', onCloseCallback = null}) {
    const { t } = useTranslation('common'); 
    const [open, setOpen] = React.useState(false);

    const handleClickOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        resetParent();
        if (onCloseCallback) onCloseCallback();
    };

    const [viewOpen, setViewOpen] = React.useState(false);
    const [formOpen, setFormOpen] = React.useState(false);
    const [formMode, setFormMode] = React.useState("create");

    const [selectedItem, setSelectedItem] = React.useState(null);

    const handleListItemClick = (item) => {
        setViewOpen(true);
        setSelectedItem(item);
    };

    const { showSnackbar } = useSnackbar();

    const { items, loading, loadMore, searchFunction, resetParent } = useInventoryItems(open, 1, 10);

    const { remove } = useDeleteInventoryItem(resetParent);

    const handleAdd = () => {
        setSelectedItem(null);
        setFormMode("create");
        setFormOpen(true);
    };

    const handleEdit = (item) => {
        setSelectedItem(item);
        setFormMode("edit");
        setFormOpen(true);
    };

    const onSaveSuccess = async (savedItem) => {
        await resetParent();
        const updatedItem = await getInventoryItemById(savedItem.id);
        setSelectedItem(updatedItem);
        if (!viewOpen) setViewOpen(true);
    };

    const handleDelete = async (item) => {
        try {
            await remove(item.id);
            showSnackbar(t('inventory.items.messages.delete_success', { name: item.name }), "success");
        } catch (err) {
            if (err.status === 409) {
                showSnackbar(t('inventory.items.messages.delete_fail_has_transaction', { name: item.name }), "error");
            } else {
                showSnackbar(t('inventory.items.messages.delete_fail', { name: item.name }), "error");
            }
        }
    };

    const [transactionViewOpen, setTransactionViewOpen] = React.useState(false);
    const [transactionFormOpen, setTransactionFormOpen] = React.useState(false);
    const [selectedTransaction, setSelectedTransaction] = React.useState(null);

    const handleCreateTransaction = (item) => {
        setSelectedTransaction(null);
        setSelectedItem(item);
        setTransactionFormOpen(true);
    };

    const onSaveTransactionSuccess = async (savedTransaction) => {
        await resetParent();
        const updatedItem = await getInventoryItemById(savedTransaction.item_id);
        setSelectedItem(updatedItem);

        const newTransaction = await getInventoryTransactionById(savedTransaction.id); 
        setSelectedTransaction(newTransaction);
        setTransactionViewOpen(true);
    };

    return (
        <React.Fragment>
          {mode === 'list' ? 
          <ListItem>
              <ListItemButton onClick={handleClickOpen}>
              <ListItemIcon>
                  <InventoryIcon fontSize='large' color='secondary'/>
              </ListItemIcon>
              <ListItemText 
                  primary="Items"
                  secondary="view and manage inventory items"
              />
              </ListItemButton>
          </ListItem>
          : null }
          {mode === 'button' ? 
          <Button onClick={handleClickOpen}>Manage Inventory</Button>
          : null }

        <Dialog onClose={handleClose} open={open} disableRestoreFocus>
            <DialogTitle>
            <Stack spacing={2}>
                <Typography variant="h6">Manage Inventory Items</Typography>
                <SearchBox searchFunction={searchFunction} resetParent={resetParent} />
                <Divider />
            </Stack>
            </DialogTitle>

            <DialogContent>
                <>
                <List sx={{ minHeight: 500 }}>
                    {items?.data?.map((item) => (
                    <ListItem
                        key={item.id}
                        secondaryAction={
                        <Box sx={{ ml: 2, display: 'flex', alignItems: 'right' }}>
                            <VerticalHamburgerMenu
                            options={[
                                { 
                                    id: 1, 
                                    name: 'View',
                                    callback: () => handleListItemClick(item),
                                    disabled: !userHasAccess(SUB_MODULES.ITEMS, 1)
                                },
                                { 
                                    id: 2, 
                                    name: 'Edit',
                                    callback: () => handleEdit(item),
                                    disabled: !userHasAccess(SUB_MODULES.ITEMS, 2)
                                },
                                { 
                                    id: 3, 
                                    name: 'Add Transaction',
                                    callback: () => handleCreateTransaction(item),
                                    disabled: !userHasAccess(SUB_MODULES.TRANSACTIONS, 2)
                                },
                                {
                                    id: 4,
                                    name: 'Delete',
                                    callback: () => handleDelete(item),
                                    confirmMessage: t('inventory.items.messages.delete_confirm', { name: `${item.name}` }),
                                    disabled: !userHasAccess(SUB_MODULES.ITEMS, 3)
                                },
                            ]}
                            />
                        </Box>
                        }
                    >
                        <ListItemButton onClick={() => {
                          if (userHasAccess(SUB_MODULES.ITEMS, 1)) handleListItemClick(item); 
                        }}>
                            <ListItemIcon>
                                <InventoryIcon fontSize='large' color='primary'/>
                            </ListItemIcon>
                            <ListItemText
                            primary={`${item.name}`}
                            secondary={ 
                            <>
                                <Typography component="span" variant="body2" color={Number(item.current_stock) <= Number(item.low_stock) ? "error" : "text.primary" } >
                                {`${removeZeroDecimals(item.current_stock)}${item.uom} in stock`}
                                </Typography><br />
                                {Number(item.current_stock) <= Number(item.low_stock) && item.supplier_notified ?
                                <>
                                <Typography component="span" variant="body2" color={Number(item.current_stock) <= Number(item.low_stock) ? "error" : "text.primary" } >
                                supplier has been notified
                                </Typography><br />
                                </>
                                : null}
                                <Typography component="span" variant="body2" color="text.secondary">
                                {item.category.name}
                                </Typography>
                                {checkExpiryDate(item.expiry_date) ?
                                <>
                                <br />
                                <Typography component="span" variant="body2" color="error">
                                {`Expiry Date: ${item.expiry_date}`}
                                </Typography>
                                </>
                                : null }
                            </>
                             }
                            />
                        </ListItemButton>
                    </ListItem>
                    ))}
                </List>

                {/* Load More only shows if more pages exist */}
                {items?.pagination?.page < items?.pagination?.totalPages && (
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <IconButton 
                            onClick={loadMore}
                            sx={{
                                width: '100%',              // fill container width
                                justifyContent: 'center',   // center the icon horizontally
                                color: 'primary.main',      // use theme's primary color
                            }}
                        >
                            <KeyboardArrowDownIcon />
                        </IconButton>
                    </Box>
                )}
                </>
            </DialogContent>
            <DialogActions>
              <RequirePermission
                subModuleName={SUB_MODULES.ITEMS}
                minAccessLevel={2}
              >
                <Button onClick={handleAdd}>Add Item</Button>
              </RequirePermission>
              <div style={{flex: '1 0 0'}} />
              <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>
        <InventoryItemView 
            handleCreateTransaction={handleCreateTransaction}
            handleEdit={handleEdit}
            item={selectedItem}
            open={viewOpen}
            onClose={() => setViewOpen(false)}
        />
        <InventoryItemForm
            successCallback={onSaveSuccess}
            item={selectedItem}
            mode={formMode}
            open={formOpen}
            onClose={() => setFormOpen(false)}
        />
        <InventoryTransactionView 
            transaction={selectedTransaction}
            open={transactionViewOpen}
            onClose={() => setTransactionViewOpen(false)}
        />
        <InventoryTransactionForm
            item={selectedItem}
            successCallback={onSaveTransactionSuccess}
            transaction={selectedTransaction}
            mode={'create'}
            open={transactionFormOpen}
            onClose={() => setTransactionFormOpen(false)}
        />
        
        </React.Fragment>
    );
}